import { defineStore } from "pinia";
import { NO_OP, useLocalStorage } from "vue-composable";
import { computed, ref, watch, type Ref } from "vue";
import * as AuthAPI from "../api/iam";
import type { AuthUser } from "../api/iam";
import { isExpired, removeExpires, updateExpires } from "../utils/session";
import axios from "../utils/axios";
import { type TenantConfig, settings } from "../api/medicus";
import { extractTenant } from "../utils/route";
import { clearCurrentPageTenant } from "../utils/delegationContext";
import { router } from "../router";
import { showDialog } from "../utils/dialog";
import { LocationQueryRaw, RouteLocationNamedRaw } from "vue-router";

// The goal for this composable is to only expose the last promise
// even when the method has been called many times,
// it also cancels previous requests
function useSettings() {
  const promise = ref<null | ReturnType<typeof settings>>(null);
  const result = ref<null | TenantConfig>(null);
  const error = ref<null | any>(null);

  let resolver = null as null | ((_: TenantConfig) => void);
  let rejects = null as null | ((_: any) => void);

  let lastRequest = 0;

  function exec() {
    if (!resolver || !rejects) {
      promise.value = new Promise<TenantConfig>((resolve, reject) => {
        resolver = resolve;
        rejects = reject;
      });
    }

    const currentRequest = ++lastRequest;

    settings({
      cancelRequestId: "app-settings",
    })
      .then((x) => {
        if (currentRequest === lastRequest) {
          result.value = x;
          error.value = null;
          resolver?.(x);
        }
      })
      .catch((e) => {
        if (currentRequest === lastRequest) {
          error.value = e;
          result.value = null;
          rejects?.(e);
        }
      })
      .finally(() => {
        if (currentRequest === lastRequest) {
          resolver = null;
          rejects = null;
        }
      });
    return promise.value;
  }

  return {
    exec,
    promise,
    result,
    error,
  };
}

export const useAuth = defineStore("auth", () => {
  const { storage: userStorage, remove: removeUser } = useLocalStorage<AuthUser | null>(
    "user",
    null,
  );
  const { storage: tenantsStorage } = useLocalStorage<AuthUser["tenants"] | null>("tenants", null);
  // ENG-21695 pinia's unwrap doesn't match vue-composable's Ref brand — re-type as vue's own Ref so
  // pinia unwraps them (runtime object unchanged).
  const user = userStorage as unknown as Ref<AuthUser | null>;
  const tenants = tenantsStorage as unknown as Ref<AuthUser["tenants"] | null>;

  // ENG-20739 Sync-hydrate from the URL so watchers that fire at store-init
  // (axios baseURL, Sentry tenant tag) see the correct value. Gated on the
  // cached tenants map so we don't trust the URL blindly.
  const initialTenant = ((): string | null => {
    if (typeof window === "undefined") return null;
    const seg = extractTenant(window.location.pathname);
    return seg && tenants.value?.[seg] ? seg : null;
  })();
  const selectedTenant = ref<string | null>(initialTenant);
  const { storage: hasMFA, remove: removeMFA } = useLocalStorage<"true" | null>("mfa", null);

  const {
    exec: refreshSettings,
    result: config,
    error: configError,
    promise: settingsPromise,
  } = useSettings();

  const userId = computed(() => user.value?.userId);

  const isLoggedIn = computed(() => !!user.value && !isExpired.value);
  const loginUrl = AuthAPI.loginUrl;

  const currentTenant = computed(() =>
    isLoggedIn.value && selectedTenant.value && tenants.value
      ? tenants.value[selectedTenant.value]
      : null,
  );

  const currentLocationValue = computed(() => {
    const u = user.value;
    if (isLoggedIn.value && u) {
      if (u.mostRecentRoomId) {
        return u.rooms?.find((r) => r.value === u.mostRecentRoomId);
      }

      if (u.mostRecentLocationId) {
        return (
          u.sites?.find((s) => s.value === u.mostRecentLocationId) ||
          u.adHocLocations?.find((l) => l.value === u.mostRecentLocationId) ||
          null
        );
      }
    }

    return null;
  });

  const hasLocations = computed(() => {
    const u = user.value;
    if (isLoggedIn.value && u) {
      return (
        (u.sites?.length ?? 0) > 0 ||
        (u.rooms?.length ?? 0) > 0 ||
        (u.adHocLocations?.length ?? 0) > 0
      );
    } else {
      return false;
    }
  });

  async function login(credentials: { username: string; password: string }) {
    removeExpires();
    localStorage.clear();
    // The page tenant lives in sessionStorage, so localStorage.clear() leaves it behind for
    // whoever signs in next on this tab.
    clearCurrentPageTenant();
    selectedTenant.value = null;

    const { data, status } = await AuthAPI.login(credentials);
    if (status !== 200) return;

    updateExpires();
    if ("mfaRequired" in data && data.mfaRequired === true) {
      hasMFA.value = "true";

      const route: RouteLocationNamedRaw = {
        name: "mfa",
        query: router.currentRoute.value.query || {},
      };

      return router.push(route);
    }
    await refreshUserInfo();
    // do redirect
    return doRedirect();
  }

  async function validateMFA(otpCode: string) {
    const { status } = await AuthAPI.loginMfa({ otpCode });
    if (status !== 200) return;

    removeMFA();
    updateExpires();
    await refreshUserInfo();

    // do redirect
    return doRedirect();
  }

  async function validateRecoveryCode(code: string) {
    const { status } = await AuthAPI.loginRecoveryCode({ code });
    if (status !== 200) return;

    removeMFA();
    updateExpires();
    await refreshUserInfo();

    // do redirect
    return doRedirect();
  }

  function reset() {
    user.value = null;
    localStorage.clear();
    clearCurrentPageTenant();
  }

  async function logout(
    redirect: boolean | string = false,
    additionalQueryParameters: object | null = null,
  ) {
    user.value = null;
    localStorage.clear();
    clearCurrentPageTenant();

    // fire and forget
    await AuthAPI.logout().catch(NO_OP);

    if (!IS_APP) return;

    const redirectTo = redirect
      ? redirect === true
        ? router.currentRoute.value.meta.public // don't redirect to public
          ? ""
          : router.currentRoute.value.path
        : redirect
      : "";
    if (
      router.currentRoute.value.name !== "login" &&
      router.currentRoute.value.name !== "unsupported"
    ) {
      let query: LocationQueryRaw = {};
      if (redirectTo) query.redirect = redirectTo;
      if (additionalQueryParameters) query = { ...query, ...additionalQueryParameters };

      location.href = router.resolve({
        name: "login",
        query,
      }).fullPath;
    }
  }

  async function refreshUserInfo() {
    const { data } = await AuthAPI.info();
    user.value = data;
    return user.value;
  }

  async function doRedirect() {
    if (settingsPromise.value) {
      await settingsPromise.value.catch(() => {});
    }
    let redirect: string | { name: string; params: any } = decodeURIComponent(
      router.currentRoute.value.query.redirect?.toString() || "/",
    );
    let tenant = extractTenant(redirect) || selectedTenant.value;

    if (!tenant) {
      const tenantIds = Object.keys(tenants.value || {});
      if (tenantIds.length === 1 && !hasLocations.value) {
        tenant = tenantIds[0];
      } else if (tenantIds.length === 0) {
        return router.push("/no-workspace-access");
      } else {
        return router.push({ name: "tenants", query: router.currentRoute.value.query });
      }
    }

    if (redirect === `/` || !redirect) {
      redirect = {
        name: "homepage",
        params: {
          tenant,
        },
      };
    }

    if (tenant) {
      selectedTenant.value = tenant;
      return router.push(redirect);
    }
  }

  watch(
    () => user.value,
    (u) => {
      if (u) {
        tenants.value = u.tenants;
      } else {
        tenants.value = {};
        selectedTenant.value = "";
        removeUser();
      }
    },
    {
      immediate: true,
      flush: "sync",
    },
  );

  watch(
    configError,
    (e) => {
      if (!e) return;
      if (e.response?.status === 401) {
        return;
      }
      showDialog({
        title: "Error",
        text: `There was an error loading the organisation’s settings.\nPlease refresh the page.`,
        noCancel: true,
        okLabel: "Refresh page",
        okColor: "secondary",
      }).onFinally(() => {
        location.reload();
      });
    },
    {
      immediate: true,
    },
  );

  watch(
    () => currentTenant.value,
    (tenant) => {
      if (!tenant) return;
      axios.defaults.baseURL = tenant.url ?? "/";
      refreshSettings();
    },
    {
      immediate: true,
      flush: "sync",
    },
  );

  return {
    currentLocationValue,
    user,
    tenants,
    selectedTenant,
    config,
    hasMFA,

    userId,
    currentTenant,
    hasLocations,
    isLoggedIn,

    loginUrl,
    login,
    reset,
    logout,

    validateMFA,
    validateRecoveryCode,

    refreshUserInfo,

    doRedirect,

    settingsPromise,
  };
});
